import { forwardRef, useImperativeHandle, useMemo } from 'react';
import {
  DropdownList,
  IconInfo,
  PhoneNumberInput,
  Stack,
  TextInput,
} from '@adc/parallax-component-library';
import { faker } from '@faker-js/faker';
import { useForm, useStore } from '@tanstack/react-form';
import * as z from 'zod';

import { DropdownInput } from '~/components/DropdownInput';
import { buildTestId } from '~/utils/buildTestId';
import { getErrorProps } from '~/utils/forms/getErrorProps';

import { idnSchema } from './validations';

export type IDNFormValues = z.infer<typeof idnSchema>;

export type IDNFormRef = {
  submitForm: () => Promise<void>;
};

type IDNFormProps = {
  testID: string;
  onSubmit: (values: IDNFormValues) => void;
  defaultValues?: Partial<IDNFormValues>;
};

const baseDefaultValues: IDNFormValues = {
  name: '',
  id: faker.string.numeric(6),
  address: '',
  addressLine2: undefined,
  city: '',
  state: '',
  zipCode: '',
  country: 'US',
  phone: undefined,
};

const countryOptions = {
  US: 'United States',
  NZ: 'New Zealand',
} as const;

const countryListItems = Object.values(countryOptions);

export const IDNForm = forwardRef<IDNFormRef, IDNFormProps>(
  ({ testID, onSubmit, defaultValues }, ref) => {
    const form = useForm({
      defaultValues: {
        ...baseDefaultValues,
        ...defaultValues,
      },
      validators: { onChange: idnSchema },
      onSubmit: ({ value }) => {
        onSubmit(idnSchema.parse(value));
      },
    });

    const countryValue = useStore(form.store, (s) => s.values.country);

    const { countrySelectedIndex, selectedCountryLabel } = useMemo(() => {
      const index = Object.keys(countryOptions).indexOf(
        countryValue as keyof typeof countryOptions,
      );
      const label = countryOptions[countryValue as keyof typeof countryOptions];

      return {
        countrySelectedIndex: index,
        selectedCountryLabel: label,
      };
    }, [countryValue]);

    useImperativeHandle(
      ref,
      () => ({
        submitForm: form.handleSubmit,
      }),
      [form.handleSubmit],
    );

    return (
      <Stack gap="$6" paddingTop="$2" testID={testID}>
        <Stack flexDirection="row" gap="$6">
          <Stack flex={1} flexShrink={1} width="50%">
            <form.Field name="name">
              {(field) => (
                <TextInput
                  testID={buildTestId({
                    testID,
                    suffix: ['input', 'name'],
                  })}
                  aria-label="IDN Name"
                  label="IDN Name"
                  value={field.state.value}
                  onChangeText={field.handleChange}
                  onBlur={field.handleBlur}
                  inputMode="text"
                  autoCapitalize="none"
                  autoCorrect={false}
                  {...getErrorProps(field)}
                />
              )}
            </form.Field>
          </Stack>
          <Stack flex={1} flexShrink={1} width="50%">
            <form.Field name="id">
              {(field) => (
                <TextInput
                  testID={buildTestId({
                    testID,
                    suffix: ['input', 'id'],
                  })}
                  aria-label="IDN ID"
                  label="IDN ID"
                  value={field.state.value}
                  onChangeText={field.handleChange}
                  onBlur={field.handleBlur}
                  inputMode="text"
                  autoCapitalize="none"
                  autoCorrect={false}
                  inlineIconConfig={{
                    icon: <IconInfo />,
                    'aria-label': 'IDN ID informations',
                    onPress: () => {
                      // TODO: add popup when content of it will be provided
                    },
                    disabledColor: '$interactive.primary.disabled.onColor',
                  }}
                  {...getErrorProps(field)}
                  disabled
                />
              )}
            </form.Field>
          </Stack>
        </Stack>
        <Stack flexDirection="row" gap="$6">
          <Stack flex={1}>
            <form.Field name="address">
              {(field) => (
                <TextInput
                  testID={buildTestId({
                    testID,
                    suffix: ['input', 'address'],
                  })}
                  aria-label="IDN Address"
                  label="IDN Address"
                  value={field.state.value}
                  onChangeText={field.handleChange}
                  onBlur={field.handleBlur}
                  inputMode="text"
                  autoCapitalize="none"
                  autoCorrect={false}
                  {...getErrorProps(field)}
                />
              )}
            </form.Field>
          </Stack>
          <Stack flex={1}>
            <form.Field name="addressLine2">
              {(field) => (
                <TextInput
                  testID={buildTestId({
                    testID,
                    suffix: ['input', 'address', 'line2'],
                  })}
                  aria-label="IDN Address Line 2 (Optional)"
                  label="IDN Address Line 2 (Optional)"
                  value={field.state.value}
                  onChangeText={field.handleChange}
                  onBlur={field.handleBlur}
                  inputMode="text"
                  autoCapitalize="none"
                  autoCorrect={false}
                  {...getErrorProps(field)}
                />
              )}
            </form.Field>
          </Stack>
        </Stack>
        <Stack flexDirection="row" gap="$6">
          <Stack flex={1}>
            <form.Field name="city">
              {(field) => (
                <TextInput
                  testID={buildTestId({
                    testID,
                    suffix: ['input', 'city'],
                  })}
                  aria-label="City"
                  label="City"
                  value={field.state.value}
                  onChangeText={field.handleChange}
                  onBlur={field.handleBlur}
                  inputMode="text"
                  autoCapitalize="none"
                  autoCorrect={false}
                  {...getErrorProps(field)}
                />
              )}
            </form.Field>
          </Stack>
          <Stack flex={1}>
            <form.Field name="state">
              {(field) => (
                <TextInput
                  testID={buildTestId({
                    testID,
                    suffix: ['input', 'state'],
                  })}
                  aria-label="State/Province"
                  label="State/Province"
                  value={field.state.value}
                  onChangeText={field.handleChange}
                  onBlur={field.handleBlur}
                  inputMode="text"
                  autoCapitalize="none"
                  autoCorrect={false}
                  {...getErrorProps(field)}
                />
              )}
            </form.Field>
          </Stack>
        </Stack>
        <Stack flexDirection="row" gap="$6">
          <Stack flex={1} flexShrink={1} width="50%">
            <form.Field name="zipCode">
              {(field) => (
                <TextInput
                  testID={buildTestId({
                    testID,
                    suffix: ['input', 'zip', 'code'],
                  })}
                  aria-label="Zip Code"
                  label="Zip Code"
                  value={field.state.value}
                  onChangeText={field.handleChange}
                  onBlur={field.handleBlur}
                  inputMode="text"
                  autoCapitalize="none"
                  autoCorrect={false}
                  {...getErrorProps(field)}
                />
              )}
            </form.Field>
          </Stack>
          <Stack flex={1} flexShrink={1} width="50%">
            <form.Field name="country">
              {(field) => (
                <DropdownList
                  testID={buildTestId({
                    testID,
                    suffix: ['dropdown', 'country'],
                  })}
                  items={countryListItems}
                  selectedIndex={countrySelectedIndex}
                  onSelect={(index) => {
                    const selectedCountryCode = Object.keys(countryOptions)[
                      index
                    ] as keyof typeof countryOptions;

                    field.setValue(selectedCountryCode);
                  }}
                >
                  <DropdownInput
                    testID={buildTestId({
                      testID,
                      suffix: ['input', 'country'],
                    })}
                    aria-label="Country"
                    label="Country"
                    value={selectedCountryLabel}
                    {...getErrorProps(field)}
                  />
                </DropdownList>
              )}
            </form.Field>
          </Stack>
        </Stack>
        <form.Field name="phone">
          {(field) => (
            <PhoneNumberInput
              testID={buildTestId({ testID, suffix: ['input', 'phone'] })}
              aria-label="Mobile Number (Optional)"
              label="Mobile Number (Optional)"
              placeholder="Phone"
              value={field.state.value}
              onChangeText={field.handleChange}
              onBlur={field.handleBlur}
              inputMode="tel"
              autoCapitalize="none"
              autoCorrect={false}
              {...getErrorProps(field)}
            />
          )}
        </form.Field>
      </Stack>
    );
  },
);

IDNForm.displayName = 'IDNForm';
